// The version checks of the pages that still write at once (not a draft, not
// an Item form): metrics, the defaults of new properties, the raw config
// editors, the decorator of the explorer, personas, the DB explorer and the
// "Save Parameters" of a conversion. Pure; the hook is useBaseVersion.js.
//
// The BASE of a page is the stored row the user started editing from: taken
// when the page loads the row, replaced when the page follows the stored row
// (clean), when the user reloads it, and by the row of our own save. The
// write sends baseVersion = the version of the base, never the version
// MemoryDB has at save time: the server refuses (409) a write whose stored
// row has another version, so a colleague's save is never overwritten.
import { sameItem } from "./itemRow";

const exists = (row) => row !== undefined && row !== null;

// The version the server compares with (api: checkVersions): a stored row
// without one is version 0, and so is a row not stored (a new item: a
// colleague's row created meanwhile is refused).
export const versionOf = (row) => (exists(row) ? Number(row.version ?? 0) : 0);

// The stored row moved under the base: another version, deleted, or created.
// Versions only (as the server): a re-read of the same row, or the same row
// with other refs (a refs.belongs cascade, refsComputed), is not a move.
export const movedUnder = (base, live) =>
  exists(base) !== exists(live) || versionOf(base) !== versionOf(live);

// What a page does when MemoryDB gives the stored row `live`:
// - "adopt": the page has no unsaved edits and the row differs: its content
//   is replaced, as before the version checks (a colleague's save is shown);
// - "stale": the page has unsaved edits and the row moved (a colleague's
//   save, or delete): the edits stay, the page tells the user, a Save would
//   be refused;
// - "keep": nothing to do (the same row, or the same version under edits).
export function onStoredRow({ base, live, dirty }) {
  if (!dirty) return sameItem(base, live) ? "keep" : "adopt";
  return movedUnder(base, live) ? "stale" : "keep";
}

// Who changed the row under the page, for the warning. live: the row now
// stored (undefined: deleted).
export const staleOf = (live) => ({
  deleted: !exists(live),
  lastSaved: live?.lastSaved
});

// The same from a refused write (409: { type, id, storedVersion, lastSaved },
// or a live conflict: { accessor, id, action }).
export const staleOfConflict = (conflict = {}) => ({
  deleted: conflict.storedVersion === null || conflict.action === "delete",
  lastSaved: conflict.lastSaved
});

// The conflict of a refused write that is about the row (accessor, id); the
// first one when none names it.
export const conflictOf = (data, accessor, id) => {
  const conflicts = data?.conflicts ?? [];
  return (
    conflicts.find(
      (c) => (c.type ?? c.accessor) === accessor && c.id === id
    ) ?? conflicts[0]
  );
};

// "Tommaso saved this meanwhile (7/10/2026, 10:12:00)", or "Deleted by a
// colleague meanwhile" (a deleted row has no lastSaved).
export function staleMessage(stale) {
  if (!stale) return "";
  if (stale.deleted) return "Deleted by a colleague meanwhile";

  const who = stale.lastSaved?.username ?? "A colleague";
  const when = stale.lastSaved?.date
    ? ` (${new Date(stale.lastSaved.date).toLocaleString()})`
    : "";
  return `${who} saved this meanwhile${when}`;
}
